<template>
	<div v-if="pageData">
		<div class="container bg-white mx-auto p-0" style="position: relative">
			<HomepageHero :pageData="pageData" ref="homepageHero" />

			<HomepageSatisfactionGuaranteed
				:pageData="pageData"
				ref="satisfactionGuaranteed"
			/>

			<!-- <HomepageReviewsCarousel :reviews="reviews" /> -->
			<!-- <Curves background="EEF1F4" foreground="fff" location="bottomRight" /> -->
			<div class="grey-bend">
				<div class="thin">
					<h2 class="h3" v-html="pageData.section_4_title" />
					<p v-html="pageData.section_4_text" />
					<p>
						<NuxtLink
							:to="pageData.section_4_button_link"
							class="button"
						>
							{{ pageData.section_4_button_text }}
						</NuxtLink>
					</p>
				</div>
			</div>
			<Curves background="EEF1F4" foreground="fff" location="topRight" />

			<Yardology :pageData="pageData" :verticle="true" />

			<div class="grid grid-cols-3 w-2/5 align-middle mx-auto py-8 items-center">
				<NuxtLink to="https://www.arborday.org/" target="_blank">
					<img
						loading="lazy"
						v-if="pageData.section_5_image_1 && pageData.section_5_image_1.url"
						:src="imageLocation + pageData.section_5_image_1.url"
						:alt="pageData.section_5_image_1.alt"
						class="hidden lg:inline-block"
					/>
				</NuxtLink>
				<NuxtLink to="https://projectevergreen.org" target="_blank">
					<img
						loading="lazy"
						v-if="pageData.section_5_image_2 && pageData.section_5_image_2.url"
						:src="imageLocation + pageData.section_5_image_2.url"
						:alt="pageData.section_5_image_2.alt"
						class="hidden lg:inline-block"
					/>
				</NuxtLink>
				<NuxtLink to="https://www.landscapeprofessionals.org/" target="_blank">
					<img
						loading="lazy"
						v-if="pageData.section_5_image_3 && pageData.section_5_image_3.url"
						:src="imageLocation + pageData.section_5_image_3.url"
						:alt="pageData.section_5_image_3.alt"
						class="hidden lg:inline-block"
					/>
				</NuxtLink>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type State from "~/domains/store/State";
import "vue3-carousel/dist/carousel.css";

// Import components
import Curves from "~/components/Curves.vue";
import HomepageHero from "~/components/Homepage/HomepageHero.vue";
import HomepageSatisfactionGuaranteed from "~/components/Homepage/SatisfactionGuaranteed.vue";
import Yardology from "~/components/Yardology.vue";

const store: Store<State> = useStore();
const router = useRouter();

const { $config, $sg, $cookiz, $userContext, $metadata } = useNuxtApp();

const reviews_slides = ref(3);
const imageLocation = computed(() => $config.public.sgCmsImage);

// Fetch page data and Organization structured data in parallel for SSR
const [{ data: pageData }, { data: orgData }] = await Promise.all([
	useAsyncData("homepage-page", () => $sg.getOctoberData("pages/homepage")),
	useAsyncData("homepage-org", () =>
		$metadata?.getOrganizationStructuredData?.() ?? Promise.resolve(null)
	),
]);

// SEO setup with WebSite and Organization schemas (all data pre-fetched)
useSeoHead({
	title:
		pageData.value?.seo_title ??
		"Local Lawn Care Services & Weed Control | Spring-Green",
	description:
		pageData.value?.meta_description ??
		"For more than 45 years, Spring Green has been providing professional lawn care, pest control and tree care services. Visit www.spring-green.com to learn more about our dedication to quality.",
	openGraph: {
		title: pageData.value?.seo_title ?? "Local Lawn Care Services & Weed Control | Spring-Green",
		description: pageData.value?.meta_description ?? "Professional lawn care, pest control and tree care services from Spring-Green.",
		type: "website",
	},
	// Homepage-specific: include WebSite and Organization structured data
	includeWebSite: true,
	orgData: orgData.value,
});

const homepageHero = useTemplateRef("homepageHero");
const satisfactionGuaranteed = useTemplateRef("satisfactionGuaranteed");

const persistedState = computed(() => store.state.persistedState);

const resize = () => {
	if (!document.getElementById("hero-cards")) {
		setTimeout(resize, 1000);
	}

	if (satisfactionGuaranteed.value) satisfactionGuaranteed.value.resize();
	if (homepageHero.value) homepageHero.value.resize();
};

onMounted(async () => {
	if (persistedState.value.franchise_slug) {
		router.push(`lawn-care-locations/${persistedState.value.franchise_slug}`);
	}

	store.state.showLoading = true;

	await $userContext.metaRefresh({
		refresh: new Set(["PAYMENT_INFO"])
	});

	await fetchData();

	store.state.showLoading = false;

	window.addEventListener("resize", resize);
	setTimeout(resize, 3000);
	if (document.body.offsetWidth < 720) {
		reviews_slides.value = 1;
	}
});

onBeforeUnmount(() => {
	window.removeEventListener("resize", resize);
});

async function fetchData() {
	var franchiseCookie = $sg.getUserFranchiseDataCookie($cookiz, "");
	if (franchiseCookie && franchiseCookie.franchise) {
		router.push(`/lawn-care-locations/${franchiseCookie.franchise}`);
		return;
	}

	let postalCode = "";

	var branchData = undefined;
	if (postalCode && postalCode !== "?")
		branchData = await $sg.getOctoberData(`branch/${postalCode}/zipcode`);
	// var reviewBranchCode = branchData && branchData.BranchCode ? branchData.BranchCode : "";
	// reviews.value = await $sg.getOctoberData(`testimonials/${reviewBranchCode}`);

	if (branchData == null || !branchData.branch || !branchData.franchise) {
		return;
	}

	var branchCode = branchData.branch.replaceAll('"', "");
	var franchiseData = await $sg.getOctoberData(`franchise/${branchCode}`);
	if (franchiseData) {
		franchiseData = franchiseData[0];
	}
	const branchInfo = JSON.parse(branchData.franchise);
	const branchLocation = franchiseData.city_state_override;
	const branchPhone = branchInfo.BranchInfo.InfoData.find(
		(data) => data.FieldName === "Business Main Phone"
	);
	const sgid = franchiseData ? franchiseData.spring_green_id : undefined;

	const persistedData = {
		sgid: sgid,
		zip: postalCode,
		branchCode: branchCode,
		branchInfo: branchInfo,
		branchLocation: branchLocation,
		branchPhone: branchPhone,
	};

	await store.commit("SET_STATE", persistedData);

	if (franchiseData !== undefined) {
		if (franchiseData.slug) {
			var cookieData = $sg.getUserFranchiseDataCookie(
				$cookiz,
				franchiseData.slug
			);
			cookieData.franchiseId = branchInfo.SQLDBID;
			cookieData.branchCode = franchiseData.spring_green_id;
			$sg.saveUserFranchiseDataCookie($cookiz, cookieData);
			store.commit("SET_FRANCHISE_SLUG", franchiseData.slug);
			router.push(`/lawn-care-locations/${franchiseData.slug}`);
		} else {
			router.push("/out-of-area");
		}
	}
}
</script>

<style scoped>
</style>
